import {FC} from "react";
import {useNavigate} from "react-router-dom";
import {Stack, IconButton, Badge, Tooltip} from "@mui/material";
import {styled} from "@mui/material/styles";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ColorModeIconDropdown from "../../ColorModeIconDropdown";
import CompanyIndicator from "./CompanyIndicator";
import {routes} from "../../../utils/routes";
import {useAppDispatch, useAppSelector} from "../../../hooks/redux";
import {useAlertTracking} from "../../../features/machinery_alert/hooks/useAlertTracking";
import {useBillingTracking} from "../../../features/billing/hooks/useBillingTracking";
import {selectUnreadCount} from "../../../features/machinery_alert/model/selectors";
import {
    selectBillingStatus,
    selectThresholdWarning,
} from "../../../features/billing/model/selectors";
import {openAlertModal} from "../../../features/machinery_alert/model/slice";
import {AlertModal} from "../../../features/machinery_alert/ui/AlertModal";
import {BillingModal} from "../../../features/billing/ui/BillingModal";
import {t} from "../../../styles/theme/tokens";
import {SIDEBAR_FOOTER_HEIGHT} from "./constants";

/**
 * ============================================================================
 * MenuPanel — подвал sidebar'a: контекст компании + глобальные действия.
 * ============================================================================
 *
 * Структура (два яруса):
 *   ┌──────────────────────────────┐
 *   │ CompanyIndicator (одна строка)│  ← название текущей компании, ellipsis
 *   ├──────────────────────────────┤
 *   │ [🔔] [💳] [🌓] [👤]          │  ← ряд глобальных действий
 *   └──────────────────────────────┘
 *
 * Почему два яруса, а не одна строка:
 *   Иконки разложены space-between на 240px. Втиснуть туда ещё и название
 *   компании (которое бывает длинным) нельзя — либо оно отберёт место у
 *   иконок, либо обрежется до бесполезности. Поэтому компания получает
 *   отдельную полную-ширину строку сверху, иконки остаются как были.
 *   Читается как «контекст → действия».
 *
 * Высота подвала увеличена с 48 до SIDEBAR_FOOTER_HEIGHT (теперь 72) под
 * второй ярус — значение централизовано в constants.
 *
 * Действия (слева направо):
 *   1. Notifications — открывает AlertModal. Badge = unread count.
 *   2. Wallet — навигация на профиль (баланс). Badge = days threshold
 *      (только в grace-периоде). Цвет иконки зависит от billing status.
 *   3. Theme switch — переключение light/dark.
 *   4. Profile — навигация на профиль (личные данные).
 *
 * onItemClick:
 *   Прокидывается из мобильного drawer'а (SideMenuMobile). Вызывается ПОСЛЕ
 *   каждого действия, чтобы drawer закрывался так же, как при клике на
 *   обычный пункт меню. На десктопе проп не передаётся (drawer permanent) —
 *   тогда это no-op. Открытие модалок (уведомления/биллинг) тоже закрывает
 *   drawer: модалка рендерится поверх, а меню больше не нужно.
 *
 * Семантика цветов wallet иконки:
 *   - restricted (баланс исчерпан) → error.main
 *   - grace (заканчивается)         → warning.main
 *   - active (норма)                → success.main
 *   - default                       → text.secondary
 * ============================================================================
 */

interface MenuPanelProps {
    /** Вызывается после любого действия — для закрытия mobile drawer'а. */
    onItemClick?: () => void;
}

const PanelRoot = styled("div")({
    height: SIDEBAR_FOOTER_HEIGHT,
    paddingInline: 12,
    paddingBlock: 8,
    flexShrink: 0,
    borderTop: `1px solid ${t.divider}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
});

const ActionsRow = styled(Stack)({
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
});

/** Цвет wallet-иконки по статусу биллинга. */
const walletColorByStatus: Record<string, string> = {
    restricted: t.error.main,
    grace: t.warning.main,
    active: t.success.main,
};

/** Общий sx для уменьшения badge'а под size="small" иконки. */
const compactBadgeSx = {
    "& .MuiBadge-badge": {
        fontSize: "0.625rem", // 10px
        height: 14,
        minWidth: 14,
        padding: "0 4px",
    },
} as const;

const MenuPanel: FC<MenuPanelProps> = ({onItemClick}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useAlertTracking();
    useBillingTracking();

    const unread = useAppSelector(selectUnreadCount);
    const billingStatus = useAppSelector(selectBillingStatus);
    const threshold = useAppSelector(selectThresholdWarning);

    const notificationsClickHandler = () => {
        dispatch(openAlertModal());
        onItemClick?.();
    };
    const walletClickHandler = () => {
        navigate(`${routes.profile}?tab=0`);
        onItemClick?.();
    };
    const profileClickHandler = () => {
        navigate(`${routes.profile}?tab=1`);
        onItemClick?.();
    };

    const walletColor = walletColorByStatus[billingStatus ?? ""] ?? t.text.secondary;

    const walletTooltip =
        billingStatus === "restricted"
            ? "Баланс исчерпан — работа ограничена"
            : billingStatus === "grace"
                ? `Баланс заканчивается (осталось ≈ ${threshold ?? "?"} дн.)`
                : billingStatus === "active"
                    ? "Баланс в порядке"
                    : "Баланс";

    return (
        <PanelRoot>
            <CompanyIndicator/>

            <ActionsRow>
                <Tooltip title="Уведомления">
                    <IconButton
                        size="small"
                        onClick={notificationsClickHandler}
                        aria-label="Уведомления"
                    >
                        <Badge
                            badgeContent={unread.count}
                            color={unread.has_critical ? "error" : "warning"}
                            max={99}
                            invisible={unread.count === 0}
                            sx={compactBadgeSx}
                        >
                            <NotificationsOutlinedIcon fontSize="small"/>
                        </Badge>
                    </IconButton>
                </Tooltip>

                {/* Переключатель темы. Закрытие drawer'а при смене темы не
                    делаем: пользователь может захотеть остаться в меню и
                    оценить его в новой теме. */}
                <ColorModeIconDropdown/>

                <Tooltip title="Профиль">
                    <IconButton
                        size="small"
                        onClick={profileClickHandler}
                        aria-label="Профиль"
                    >
                        <AccountCircleOutlinedIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>

                <Tooltip title={walletTooltip}>
                    <IconButton
                        size="small"
                        onClick={walletClickHandler}
                        aria-label="Баланс"
                    >
                        <Badge
                            badgeContent={threshold ?? 0}
                            color="warning"
                            max={9}
                            invisible={billingStatus !== "grace"}
                            sx={compactBadgeSx}
                        >
                            <AccountBalanceWalletOutlinedIcon
                                fontSize="small"
                                sx={{color: walletColor}}
                            />
                        </Badge>
                    </IconButton>
                </Tooltip>
            </ActionsRow>

            <AlertModal/>
            <BillingModal/>
        </PanelRoot>
    );
};

export default MenuPanel;